/* ──────────────────────────────────────────────────────────
   Hero Banner HH — Carousel Logic
   - RTL aware
   - Autoplay with pause-on-hover and reduced-motion respect
   - Keyboard support
   - Touch/swipe
   ────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  const SELECTOR = '.hh-hero';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class HHHero {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-track]');
      this.slides = Array.from(root.querySelectorAll('[data-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-dot]'));
      this.prevBtn = root.querySelector('[data-prev]');
      this.nextBtn = root.querySelector('[data-next]');

      this.isRTL = getComputedStyle(root).direction === 'rtl';
      this.index = 0;
      this.total = this.slides.length;
      this.autoplay = root.dataset.autoplay === 'true' && !reduceMotion;
      this.autoplayMs = Number(root.dataset.autoplaySeconds || 6) * 1000;
      this.timer = null;

      if (this.total <= 1) return;

      this.bind();
      this.go(0, { instant: true });
      this.start();
      this.observeVisibility();
    }

    bind() {
      this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const i = Number(dot.dataset.dot);
          if (!Number.isNaN(i)) this.go(i);
        });
      });

      this.root.addEventListener('mouseenter', () => this.stop());
      this.root.addEventListener('mouseleave', () => this.start());
      this.root.addEventListener('focusin', () => this.stop());
      this.root.addEventListener('focusout', () => this.start());

      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.go(this.isRTL ? this.index + 1 : this.index - 1);
        } else if (e.key === 'ArrowRight') {
          this.go(this.isRTL ? this.index - 1 : this.index + 1);
        }
      });

      let startX = 0;
      let deltaX = 0;
      let dragging = false;

      const onStart = (e) => {
        dragging = true;
        startX = (e.touches ? e.touches[0].clientX : e.clientX);
        this.stop();
      };
      const onMove = (e) => {
        if (!dragging) return;
        const x = (e.touches ? e.touches[0].clientX : e.clientX);
        deltaX = x - startX;
      };
      const onEnd = () => {
        if (!dragging) return;
        dragging = false;
        const threshold = 50;
        if (Math.abs(deltaX) > threshold) {
          const dirNext = this.isRTL ? deltaX > 0 : deltaX < 0;
          this.go(dirNext ? this.index + 1 : this.index - 1);
        }
        deltaX = 0;
        this.start();
      };

      this.root.addEventListener('touchstart', onStart, { passive: true });
      this.root.addEventListener('touchmove', onMove, { passive: true });
      this.root.addEventListener('touchend', onEnd);

      window.addEventListener('resize', () => this.go(this.index, { instant: true }));
    }

    go(target, { instant = false } = {}) {
      if (target < 0) target = this.total - 1;
      if (target >= this.total) target = 0;
      this.index = target;

      const pct = this.isRTL ? this.index * 100 : -this.index * 100;
      this.track.style.transition = instant ? 'none' : '';
      this.track.style.transform = `translateX(${pct}%)`;

      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });

      if (instant) {
        this.track.offsetHeight;
        this.track.style.transition = '';
      }
    }

    start() {
      if (!this.autoplay) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.autoplayMs);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.start();
          else this.stop();
        });
      }, { threshold: 0.1 });
      io.observe(this.root);
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach((el) => {
      if (el.__hhHero) return;
      el.__hhHero = new HHHero(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
